import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, Phone, Mail, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { ServiceCard } from '../components/ServiceCard';
import { useLanguage } from '../context/LanguageContext';
import { ObfuscatedPhone } from '../components/ObfuscatedPhone';
import { ObfuscatedInfoEmail } from '../components/ObfuscatedInfoEmail';
import { GoogleBusinessSection } from '../components/GoogleBusiness';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Construction gallery images - order: wooden kitchen, coffee bar, blue cabinets, workspace, custom desk
const CONSTRUCTION_GALLERY = [
  'https://customer-assets.emergentagent.com/job_kenscho-builder/artifacts/x8jwsat8_C0A8DCD0-6917-47A8-9104-9512620B285A_1_105_c.jpeg',
  'https://customer-assets.emergentagent.com/job_kenscho-builder/artifacts/4hgr5q9d_930CBD97-7427-4592-B3CC-E7C4B02AA0FF_1_105_c.jpeg',
  'https://customer-assets.emergentagent.com/job_kenscho-builder/artifacts/i1l26ti1_2B6CBBE4-C7B7-4A6A-8265-3FAD29A39D08_1_105_c.jpeg',
  'https://customer-assets.emergentagent.com/job_kenscho-builder/artifacts/79ndz9gw_297C928A-E9D0-4B84-BCFB-32F829B522E3_1_105_c.jpeg',
  'https://customer-assets.emergentagent.com/job_kenscho-builder/artifacts/mvis0tyo_B95FE351-5831-4423-A52A-62557DE74338_1_105_c.jpeg',
];

// Commercial/Residential gallery - construction site images
const PROJECT_GALLERY = [
  'https://customer-assets.emergentagent.com/job_kenscho-builder/artifacts/gfol4p56_6B0C59C6-5FAA-43D6-893C-7DF406F7C474_1_105_c.jpeg',
  'https://customer-assets.emergentagent.com/job_kenscho-builder/artifacts/vcyfsw80_36A272E0-6EA8-4CD3-AF89-B79904983587_1_105_c.jpeg',
  'https://customer-assets.emergentagent.com/job_kenscho-builder/artifacts/t3ov9apr_AFEEF54A-E72F-475E-B70C-62B0B49B3D9A_1_105_c.jpeg',
  'https://customer-assets.emergentagent.com/job_kenscho-builder/artifacts/u9w11wku_B1DFAAA8-CF5E-48C1-8FBA-56D1C4A085A3_1_105_c.jpeg',
  'https://customer-assets.emergentagent.com/job_kenscho-builder/artifacts/awl4aer3_EBAF4C41-8B43-4A89-97C6-32806EE8A09C_1_105_c.jpeg',
];

export default function Services() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [galleryIndex, setGalleryIndex] = useState(0);
  const [projectIndex, setProjectIndex] = useState(0);
  const { t, language, getLocalizedLink } = useLanguage();

  const nextGalleryImage = () => setGalleryIndex((prev) => (prev + 1) % CONSTRUCTION_GALLERY.length);
  const prevGalleryImage = () => setGalleryIndex((prev) => (prev - 1 + CONSTRUCTION_GALLERY.length) % CONSTRUCTION_GALLERY.length);
  
  const nextProjectImage = () => setProjectIndex((prev) => (prev + 1) % PROJECT_GALLERY.length);
  const prevProjectImage = () => setProjectIndex((prev) => (prev - 1 + PROJECT_GALLERY.length) % PROJECT_GALLERY.length);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await axios.get(`${API}/services`);
        setServices(response.data);
      } catch (error) {
        console.error('Error fetching services:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchServices();
  }, []);

  const serviceAreas = language === 'fr' 
    ? [
        { 
          name: 'Lanaudière', 
          desc: t('services.fullRegion'),
          cities: ['Rawdon', 'Saint-Calixte', 'Joliette', 'Terrebonne', 'Mascouche', 'Repentigny']
        },
        { 
          name: 'Laurentides', 
          desc: t('services.southRegion'),
          cities: ['St-Jérôme', 'Saint-Sauveur', 'Mirabel', 'Lachute', 'Mont-Tremblant', 'Blainville']
        },
        { 
          name: 'Laval', 
          desc: t('services.completeCoverage'),
          cities: ['Chomedey', 'Vimont', 'Duvernay', 'Sainte-Rose', 'Fabreville', 'Pont-Viau']
        },
        { 
          name: 'Montréal', 
          desc: t('services.allNeighborhoods'),
          cities: ['Plateau', 'Rosemont', 'Villeray', 'Ahuntsic', 'Saint-Laurent', 'Côte-des-Neiges']
        }
      ]
    : [
        { 
          name: 'Lanaudière', 
          desc: t('services.fullRegion'),
          cities: ['Rawdon', 'Saint-Calixte', 'Joliette', 'Terrebonne', 'Mascouche', 'Repentigny']
        },
        { 
          name: 'Laurentians', 
          desc: t('services.southRegion'),
          cities: ['St-Jérôme', 'Saint-Sauveur', 'Mirabel', 'Lachute', 'Mont-Tremblant', 'Blainville']
        },
        { 
          name: 'Laval', 
          desc: t('services.completeCoverage'),
          cities: ['Chomedey', 'Vimont', 'Duvernay', 'Sainte-Rose', 'Fabreville', 'Pont-Viau']
        },
        { 
          name: 'Montreal', 
          desc: t('services.allNeighborhoods'),
          cities: ['Plateau', 'Rosemont', 'Villeray', 'Ahuntsic', 'Saint-Laurent', 'Côte-des-Neiges']
        }
      ];

  // SEO Structured Data for Construction Services
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "GeneralContractor",
    "name": "Kenscho Design",
    "image": "https://customer-assets.emergentagent.com/job_construction-hub-125/artifacts/hiq4o5va_Logo%20001%20-%20TR%20Kenscho%20Design.png",
    "telephone": "+1-514-291-3737",
    "email": "Info@Kenscho.ca",
    "address": {
      "@type": "PostalAddress",
      "addressRegion": "QC",
      "addressCountry": "CA"
    },
    "areaServed": [
      { "@type": "Place", "name": "Lanaudière" },
      { "@type": "Place", "name": "Laurentians" },
      { "@type": "Place", "name": "Laval" },
      { "@type": "Place", "name": "Montreal" },
      { "@type": "Place", "name": "Rawdon" },
      { "@type": "Place", "name": "St-Jérôme" },
      { "@type": "Place", "name": "Mirabel" },
      { "@type": "Place", "name": "Terrebonne" }
    ],
    "priceRange": "$$",
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": language === 'fr' ? "Services de Construction" : "Construction Services",
      "itemListElement": [
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": language === 'fr' ? "Rénovation résidentielle" : "Residential Renovation" }},
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": language === 'fr' ? "Construction commerciale" : "Commercial Construction" }},
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": language === 'fr' ? "Construction industrielle" : "Industrial Construction" }}
      ]
    }
  };

  return (
    <div className="grain-texture pt-20" data-testid="services-page">
      <Helmet>
        <title>{language === 'fr' 
          ? 'Construction et Rénovation | Résidentiel, Commercial, Industriel | Lanaudière, Laurentides, Laval, Montréal | Kenscho'
          : 'Construction & Renovation | Residential, Commercial, Industrial | Lanaudière, Laurentians, Laval, Montreal | Kenscho'}
        </title>
        <meta name="description" content={language === 'fr'
          ? 'Services de construction et rénovation à Lanaudière, Laurentides, Laval et Montréal. Résidentiel, commercial et industriel. Rawdon, St-Jérôme, Mirabel, Terrebonne, Joliette. RBQ 5829 4596 01.'
          : 'Construction and renovation services in Lanaudière, Laurentians, Laval, and Montreal. Residential, commercial & industrial. Rawdon, St-Jérôme, Mirabel, Terrebonne, Joliette. RBQ 5829 4596 01.'} />
        <meta name="keywords" content={language === 'fr'
          ? 'construction, rénovation, entrepreneur général, Lanaudière, Laurentides, Laval, Montréal, Rawdon, St-Jérôme, Mirabel, Terrebonne, Joliette, Saint-Sauveur, Mont-Tremblant, résidentiel, commercial, industriel'
          : 'construction, renovation, general contractor, Lanaudière, Laurentians, Laval, Montreal, Rawdon, St-Jerome, Mirabel, Terrebonne, Joliette, Saint-Sauveur, Mont-Tremblant, residential, commercial, industrial'} />
        <link rel="canonical" href={`https://www.kenscho.ca${getLocalizedLink('/services')}`} />
        
        {/* Hreflang Tags */}
        <link rel="alternate" hreflang="en" href="https://www.kenscho.ca/services" />
        <link rel="alternate" hreflang="fr" href="https://www.kenscho.ca/fr/services" />
        <link rel="alternate" hreflang="x-default" href="https://www.kenscho.ca/services" />
        
        {/* Open Graph */}
        <meta property="og:title" content={language === 'fr' ? 'Construction et Rénovation | Kenscho Design' : 'Construction & Renovation | Kenscho Design'} />
        <meta property="og:description" content={language === 'fr'
          ? 'Services de construction professionnels. Résidentiel, commercial, industriel. Lanaudière, Laurentides, Laval, Montréal.'
          : 'Professional construction services. Residential, commercial, industrial. Lanaudière, Laurentians, Laval, Montreal.'} />
        <meta property="og:url" content={`https://www.kenscho.ca${getLocalizedLink('/services')}`} />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content={language === 'fr' ? 'fr_CA' : 'en_CA'} />
        <meta property="og:image" content="https://customer-assets.emergentagent.com/job_kenscho-builder/artifacts/x8jwsat8_C0A8DCD0-6917-47A8-9104-9512620B285A_1_105_c.jpeg" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={language === 'fr' ? 'Construction et Rénovation | Kenscho Design' : 'Construction & Renovation | Kenscho Design'} />
        
        {/* Geo Tags */}
        <meta name="geo.region" content="CA-QC" />
        <meta name="geo.placename" content="Lanaudière, Laurentians, Laval, Montreal" />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      {/* Hero */}
      <section className="py-20 bg-muted/30">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            <p className="text-sm font-medium tracking-widest uppercase text-primary mb-4 animate-fade-in-up">
              {t('services.tagline')}
            </p>
            <h1 className="text-5xl md:text-6xl font-light tracking-tight mb-6 animate-fade-in-up animation-delay-100">
              {t('services.title1')}<br />
              <span className="text-primary">{t('services.title2')}</span>
            </h1>
            <p className="text-lg text-muted-foreground leading-relaxed animate-fade-in-up animation-delay-200">
              {t('services.desc')}
            </p>
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-8">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="bg-primary/10 border border-primary/20 rounded-xl p-6 md:p-8">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
              <div>
                <h3 className="text-2xl font-light mb-2">
                  {language === 'fr' ? 'Prêt à commencer?' : 'Ready to get started?'}
                </h3>
                <p className="text-muted-foreground">
                  {language === 'fr' 
                    ? 'Appelez maintenant pour planifier une consultation ou obtenir un devis pour votre projet de construction.'
                    : 'Call now to schedule a consultation or get a quote for your construction project.'}
                </p>
              </div>
              <div className="flex flex-col sm:flex-row items-center gap-6">
                <div className="flex items-center gap-6">
                  <div className="flex items-center gap-2 whitespace-nowrap">
                    <Phone className="h-5 w-5 text-primary flex-shrink-0" />
                    <ObfuscatedPhone className="font-medium whitespace-nowrap" />
                  </div>
                  <div className="flex items-center gap-2">
                    <Mail className="h-5 w-5 text-primary flex-shrink-0" />
                    <ObfuscatedInfoEmail className="font-medium" />
                  </div>
                </div>
                <Button asChild className="rounded-full btn-animate">
                  <Link to={getLocalizedLink('/contact')}>
                    {language === 'fr' ? 'Obtenir un devis' : 'Get a Quote'} <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="bg-muted animate-pulse h-80 rounded-lg" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 auto-rows-fr">
              {services.map((service, index) => (
                <div key={service.id} className="stagger-item h-full">
                  <ServiceCard service={service} />
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Gallery Carousel Section */}
      <section className="py-16 bg-foreground text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Text Content */}
            <div>
              <p className="text-sm font-medium tracking-widest uppercase text-primary mb-4">
                {language === 'fr' ? 'Notre travail' : 'Our Work'}
              </p>
              <h2 className="text-4xl md:text-5xl font-light tracking-tight mb-6">
                {language === 'fr' ? 'Artisanat de qualité' : 'Quality Craftsmanship'}
              </h2>
              <p className="text-white/70 text-lg leading-relaxed mb-8">
                {language === 'fr' 
                  ? 'Des cuisines personnalisées aux rénovations commerciales, nous apportons précision et attention aux détails à chaque projet. Parcourez certaines de nos réalisations récentes.'
                  : 'From custom kitchens to commercial buildouts, we bring precision and attention to detail to every project. Browse some of our recent work.'}
              </p>
              <Button asChild variant="secondary" className="rounded-full">
                <Link to={getLocalizedLink('/contact')}>
                  {language === 'fr' ? 'Discuter de votre projet' : 'Discuss Your Project'} <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>

            {/* Image Carousel */}
            <div className="relative group">
              <div className="aspect-[4/3] rounded-xl overflow-hidden bg-muted">
                <img 
                  src={CONSTRUCTION_GALLERY[galleryIndex]} 
                  alt={`Construction project ${galleryIndex + 1}`}
                  className="w-full h-full object-cover transition-transform duration-500"
                />
              </div>
              
              {/* Navigation Arrows */}
              <button 
                onClick={prevGalleryImage}
                className="absolute left-4 top-1/2 -translate-y-1/2 p-3 bg-black/50 rounded-full text-white hover:bg-black/70 transition-colors"
                aria-label="Previous image"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <button 
                onClick={nextGalleryImage}
                className="absolute right-4 top-1/2 -translate-y-1/2 p-3 bg-black/50 rounded-full text-white hover:bg-black/70 transition-colors"
                aria-label="Next image"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
              
              {/* Counter */}
              <div className="absolute bottom-4 right-4 bg-black/50 text-white text-sm px-3 py-1.5 rounded-full">
                {galleryIndex + 1} / {CONSTRUCTION_GALLERY.length}
              </div>

              {/* Thumbnails */}
              <div className="flex justify-center gap-2 mt-4">
                {CONSTRUCTION_GALLERY.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setGalleryIndex(idx)}
                    className={`w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                      idx === galleryIndex 
                        ? 'border-primary ring-2 ring-primary/30' 
                        : 'border-transparent opacity-60 hover:opacity-100'
                    }`}
                  >
                    <img 
                      src={img} 
                      alt={`Thumbnail ${idx + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Residential & Commercial Section - Mirrored Layout */}
      <section className="py-16 bg-muted/30">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Image Carousel - Left Side */}
            <div className="relative group order-2 lg:order-1">
              <div className="aspect-[4/3] rounded-xl overflow-hidden bg-muted">
                <img 
                  src={PROJECT_GALLERY[projectIndex]} 
                  alt={`Project ${projectIndex + 1}`}
                  className="w-full h-full object-cover transition-transform duration-500"
                />
              </div>
              
              {/* Navigation Arrows */}
              <button 
                onClick={prevProjectImage}
                className="absolute left-4 top-1/2 -translate-y-1/2 p-3 bg-black/50 rounded-full text-white hover:bg-black/70 transition-colors"
                aria-label="Previous image"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <button 
                onClick={nextProjectImage}
                className="absolute right-4 top-1/2 -translate-y-1/2 p-3 bg-black/50 rounded-full text-white hover:bg-black/70 transition-colors"
                aria-label="Next image"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
              
              {/* Counter */}
              <div className="absolute bottom-4 right-4 bg-black/50 text-white text-sm px-3 py-1.5 rounded-full">
                {projectIndex + 1} / {PROJECT_GALLERY.length}
              </div>

              {/* Thumbnails */}
              <div className="flex justify-center gap-2 mt-4">
                {PROJECT_GALLERY.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setProjectIndex(idx)}
                    className={`w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                      idx === projectIndex 
                        ? 'border-primary ring-2 ring-primary/30' 
                        : 'border-transparent opacity-60 hover:opacity-100'
                    }`}
                  >
                    <img 
                      src={img} 
                      alt={`Thumbnail ${idx + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            </div>

            {/* Text Content - Right Side */}
            <div className="order-1 lg:order-2">
              <p className="text-sm font-medium tracking-widest uppercase text-primary mb-4">
                {language === 'fr' ? 'Résidentiel & Commercial' : 'Residential & Commercial'}
              </p>
              <h2 className="text-4xl md:text-5xl font-light tracking-tight mb-6">
                {language === 'fr' ? 'Aucun projet trop grand ou trop petit' : 'No Project Too Big or Small'}
              </h2>
              <p className="text-muted-foreground text-lg leading-relaxed mb-6">
                {language === 'fr' 
                  ? 'Que vous rénoviez votre cuisine ou construisiez un immeuble commercial de plusieurs étages, notre équipe a l\'expertise et l\'équipement pour réaliser votre vision.'
                  : 'Whether you\'re renovating your kitchen or building a multi-story commercial complex, our team has the expertise and equipment to bring your vision to life.'}
              </p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-3 text-muted-foreground">
                  <span className="w-2 h-2 bg-primary rounded-full"></span>
                  {language === 'fr' ? 'Rénovations résidentielles personnalisées' : 'Custom residential renovations'}
                </li>
                <li className="flex items-center gap-3 text-muted-foreground">
                  <span className="w-2 h-2 bg-primary rounded-full"></span>
                  {language === 'fr' ? 'Constructions commerciales à grande échelle' : 'Large-scale commercial builds'}
                </li>
                <li className="flex items-center gap-3 text-muted-foreground">
                  <span className="w-2 h-2 bg-primary rounded-full"></span>
                  {language === 'fr' ? 'Projets industriels et institutionnels' : 'Industrial & institutional projects'}
                </li>
                <li className="flex items-center gap-3 text-muted-foreground">
                  <span className="w-2 h-2 bg-primary rounded-full"></span>
                  {language === 'fr' ? 'Équipement spécialisé pour chaque défi' : 'Specialized equipment for every challenge'}
                </li>
              </ul>
              <Button asChild className="rounded-full btn-animate">
                <Link to={getLocalizedLink('/contact')}>
                  {language === 'fr' ? 'Obtenir un devis' : 'Get a Quote'} <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <section className="py-24 bg-muted/30">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-sm font-medium tracking-widest uppercase text-primary mb-4">
                {t('services.serviceAreas')}
              </p>
              <h2 className="text-4xl md:text-5xl font-light tracking-tight mb-8">
                {t('services.whereWeWork')}
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed mb-8">
                {t('services.areaDesc')}
              </p>
              
              <div className="grid grid-cols-2 gap-6">
                {serviceAreas.map((area) => (
                  <div key={area.name} className="p-6 bg-card border border-border/60 rounded-lg">
                    <h3 className="text-xl font-medium mb-1">{area.name}</h3>
                    <p className="text-muted-foreground text-sm mb-3">{area.desc}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {area.cities.map((city) => (
                        <span key={city} className="text-xs px-2 py-0.5 bg-muted rounded-full text-muted-foreground">
                          {city}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="aspect-square rounded-lg overflow-hidden bg-muted">
              <img 
                src="https://images.unsplash.com/photo-1497366216548-37526070297c?w=800"
                alt="Montreal cityscape"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h2 className="text-3xl md:text-4xl font-light tracking-tight mb-6">
            {t('services.projectInMind')}
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-10">
            {t('services.contactForQuote')}
          </p>
          <Button asChild size="lg" className="rounded-full px-8 btn-animate" data-testid="services-cta-btn">
            <Link to={getLocalizedLink('/contact')}>
              {t('services.getFreeQuote')} <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </Button>
        </div>
      </section>

      {/* Google Business Section - Kenscho Design */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <GoogleBusinessSection profile="design" showMap={true} />
        </div>
      </section>
    </div>
  );
}
